import "./index.css";
import {
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  useFloatingNodeId,
} from "@floating-ui/react";
import { type MouseEvent, useMemo } from "react";
import { composeEventHandlers, Slot } from "../../utils";
import { DropdownProvider } from "./context";
import type { DropdownContextValue, DropdownProps } from "./types";
import { useViewModel } from "./useViewModel";
import { clsx } from "clsx";

export const Dropdown = (props: DropdownProps) => {
  const {
    trigger,
    content,
    floatingFocusManager,
    contentClassName,
    ariaLabel,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
  } = props;

  const {
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    open,
    setOpen,
    dismiss,
    placement,
    activeIndex,
    setActiveIndex,
    navigationRef,
    triggerId,
    contentId,
  } = useViewModel(props);

  const nodeId = useFloatingNodeId();

  const contextValue = useMemo((): DropdownContextValue => {
    return {
      open,
      setOpen,
      placement,
      floatingStyles,
      refs,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
      setActiveIndex,
      navigationRef,
      context,
      dismiss,
      triggerId,
      contentId,
      props,
    };
  }, [
    open,
    setOpen,
    placement,
    floatingStyles,
    refs,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    activeIndex,
    setActiveIndex,
    navigationRef,
    context,
    dismiss,
    triggerId,
    contentId,
    props,
  ]);

  const { role } = props;
  const referenceProps = getReferenceProps();

  const preventEverything = (e: MouseEvent) => {
    if (stopPropagationOnTrigger) {
      e.stopPropagation();
    }
    if (preventDefaultOnTrigger) {
      e.preventDefault();
    }
  };

  const triggerElement = useMemo(() => {
    const { onClick: referenceOnClick, ...otherReferenceProps } =
      referenceProps;

    const composedOnClick = composeEventHandlers(
      referenceOnClick as (e: MouseEvent) => void,
      preventEverything,
    );

    return (
      <Slot
        ref={(node: HTMLElement | null) => refs.setReference(node)}
        id={triggerId}
        tabIndex={0}
        className="nuco-dropdown__trigger-element"
        onClick={composedOnClick}
        {...otherReferenceProps}
      >
        {trigger}
      </Slot>
    );
  }, [
    trigger,
    refs.setReference,
    triggerId,
    referenceProps,
    preventEverything,
  ]);

  const contentElement = useMemo(() => {
    if (!open) return null;

    const floatingContent = (
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, opacity: floatingStyles.transform ? 1 : 0 }}
        className={clsx("nuco-dropdown__content-element", contentClassName)}
        aria-label={ariaLabel}
        aria-modal={floatingFocusManager?.modal ? "true" : undefined}
        {...getFloatingProps()}
        id={contentId}
        role={role}
      >
        {content}
      </div>
    );

    return (
      <FloatingFocusManager context={context} {...floatingFocusManager}>
        {floatingContent}
      </FloatingFocusManager>
    );
  }, [
    open,
    contentId,
    refs.setFloating,
    floatingStyles,
    contentClassName,
    ariaLabel,
    getFloatingProps,
    floatingFocusManager,
    context,
    content,
    role,
  ]);

  return (
    <DropdownProvider value={contextValue}>
      <FloatingNode id={nodeId}>
        <>{triggerElement}</>
        <FloatingPortal>{contentElement}</FloatingPortal>
      </FloatingNode>
    </DropdownProvider>
  );
};
